import { motion } from "framer-motion";
import {
  Globe,
  Boxes,
  Search,
  Ship,
  ClipboardCheck,
  HeartHandshake,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { useEnquiry } from "../components/EnquiryModal";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../data/content";

const FOCUS_ICONS = [Globe, Boxes, Search, Ship, ClipboardCheck, HeartHandshake];

export default function ExportPage() {
  const { open } = useEnquiry();
  const { t } = useLanguage();
  const focus = t("exportPage.focus");
  const nodes = t("exportPage.nodes");

  return (
    <main>
      <PageMeta
        title="Food Exporter Dubai | Export to Africa & Global Markets – Al Nama Importer"
        description="From its base in the UAE, Al Nama Importer supports international trade and export opportunities across Africa and other global destinations."
        titleAr="مصدّر أغذية في دبي | التصدير إلى أفريقيا والأسواق العالمية – النعم للاستيراد"
        descriptionAr="من قاعدتها في الإمارات، تدعم النعم للاستيراد فرص التجارة الدولية والتصدير إلى أفريقيا ووجهات عالمية أخرى."
      />
      <PageHero
        image={IMAGES.dubaiPort}
        overline={t("exportPage.heroOver")}
        titleLines={[t("exportPage.heroL1"), t("exportPage.heroL2")]}
        text={t("exportPage.heroText")}
      />

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            overline={t("exportPage.focusOver")}
            title={
              <>
                {t("exportPage.focusTitleA")} <span className="italic text-gold-gradient">{t("exportPage.focusTitleB")}</span>
              </>
            }
          />
          <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {focus.map((f, i) => {
              const Icon = FOCUS_ICONS[i];
              return (
                <Reveal key={f} delay={(i % 3) * 0.08}>
                  <div
                    data-testid={`export-focus-${i}`}
                    className="group flex h-full items-center gap-5 rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-6 transition-all duration-500 hover:border-amber-500/45 hover:bg-[#0F1E18]"
                  >
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/30 text-amber-400 transition-colors duration-500 group-hover:bg-amber-500/10">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <p className="font-display font-medium text-emerald-50">{f}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0B1D16] py-24 sm:py-32">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHeading
            align="center"
            overline={t("exportPage.routeOver")}
            title={
              <>
                {t("exportPage.routeTitleA")} <span className="italic text-gold-gradient">← →</span> {t("exportPage.routeTitleB")}
              </>
            }
          />
          <div className="mt-20 flex flex-col items-center gap-6 lg:flex-row lg:gap-0">
            {nodes.map((node, i) => (
              <div key={node} className="flex w-full flex-col items-center gap-6 lg:flex-row lg:gap-0">
                <motion.div
                  data-testid={`export-flow-node-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.25, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className={`w-full rounded-lg border p-8 text-center ${
                    i === 1
                      ? "border-amber-500/50 bg-[#0F1E18] shadow-[0_0_50px_rgba(212,175,55,0.12)]"
                      : "border-amber-500/15 bg-[#09121F]/70"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] font-display font-semibold text-amber-400">
                    {i === 0 ? t("exportPage.origin") : i === 1 ? t("exportPage.hub") : t("exportPage.dest")}
                  </p>
                  <p className="mt-3 font-serif text-xl font-semibold text-emerald-50 sm:text-2xl">{node}</p>
                </motion.div>
                {i < nodes.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.25 + 0.2 }}
                    className="flex shrink-0 items-center justify-center lg:px-4"
                  >
                    <ChevronRight className="h-8 w-8 rotate-90 text-amber-500/70 lg:rotate-0 rtl:lg:rotate-180" strokeWidth={1.2} />
                  </motion.div>
                )}
              </div>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-16 text-center">
              <button
                data-testid="export-start-enquiry-btn"
                onClick={() => open({ type: "Export Requirement" })}
                className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-105"
              >
                {t("exportPage.cta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
